/**
 * The units a duration is edited in, largest first.
 */
export const durationUnits = [
  { suffix: 'd', label: 'Days', seconds: 86_400 },
  { suffix: 'h', label: 'Hours', seconds: 3_600 },
  { suffix: 'm', label: 'Minutes', seconds: 60 },
] as const

/**
 * Splits whole seconds into days, hours and minutes, in the order of
 * {@link durationUnits}. Leftover seconds aren't shown.
 *
 * @param seconds
 */
export function toDurationParts(seconds: number): number[] {
  const total = wholeSeconds(seconds)
  return durationUnits.map((unit, i) => {
    const remainder = i === 0 ? total : total % durationUnits[i - 1].seconds
    return Math.floor(remainder / unit.seconds)
  })
}

/**
 * The duration with one unit's part replaced. The other parts and the
 * leftover seconds stay as they are.
 *
 * @param seconds The current duration.
 * @param unitIndex The index into {@link durationUnits}.
 * @param part The unit's new part; negative counts as 0.
 */
export function withDurationPart(
  seconds: number,
  unitIndex: number,
  part: number,
): number {
  const total = wholeSeconds(seconds)
  const leftover = total % 60
  return toDurationParts(total)
    .map((p, i) => (i === unitIndex ? Math.max(0, Math.floor(part)) : p))
    .reduce((sum, p, i) => sum + p * durationUnits[i].seconds, leftover)
}

/**
 * The duration with one unit's part stepped by `delta`. Stepping past a
 * unit's range carries into the next larger unit or borrows from it, like
 * 59m + 1 = 1h 0m. The duration doesn't go below 0.
 *
 * @param seconds The current duration.
 * @param unitIndex The index into {@link durationUnits}.
 * @param delta How many of the unit to add; negative to subtract.
 */
export function stepDurationPart(
  seconds: number,
  unitIndex: number,
  delta: number,
): number {
  return Math.max(
    0,
    wholeSeconds(seconds) + delta * durationUnits[unitIndex].seconds,
  )
}

function wholeSeconds(seconds: number) {
  return Math.max(0, Math.floor(seconds || 0))
}
